/**
 * Warm a link before it is pressed: once the pointer rests on an in-app link, or focus comes to it, its screen's code
 * and the reads the screen opens on are fetched, so the press shows the screen at once. Only how a screen opens is
 * warmed; `prefetch` asks nothing for what is fresh or already on its way.
 */
import { EVENTS_KEY, eventKey, getEvent, getEvents } from "../data/events";
import { prefetch } from "../data/useQuery";
import { inAppUrl } from "./links";
import { screenLoaders } from "./screens";

/** A pointer passing over links on its way elsewhere warms none of them: it must rest this long. */
const REST_MS = 60;

const SCREEN: Record<string, keyof typeof screenLoaders> = { "": "today", people: "people", events: "events", email: "email", money: "money", settings: "settings" };

const quiet = () => undefined;

/** Ask for each read, by key and fetcher. */
const ask = (reads: ReadonlyArray<readonly [string, () => Promise<unknown>]>) => reads.forEach(([key, fetcher]) => prefetch(key, fetcher));

function warm(url: URL): void {
  const [, job = "", ref] = url.pathname.split("/");
  const screen = SCREEN[job];
  if (!screen) return;
  screenLoaders[screen]().catch(quiet);
  if (job === "people") {
    void import("../screens/people/warm").then((m) => m.warm(url.searchParams), quiet);
  } else if (job === "events") {
    const event = ref && decodeURIComponent(ref);
    if (event) prefetch(eventKey(event), () => getEvent(event));
    else prefetch(EVENTS_KEY, getEvents);
  } else if (!url.search && job === "money") {
    void import("../screens/money/model").then((m) => ask(m.OPENING), quiet);
  } else if (job === "settings" && ref === "imports") {
    void import("../screens/settings/imports/model").then((m) => ask(m.OPENING), quiet);
  } else if (!url.search && job === "") {
    void import("../screens/today/useToday").then((m) => ask(m.opening()), quiet);
  }
}

let resting = 0;

/** For pointerover: warm the link under the pointer once it rests there. */
export function warmOnRest(target: EventTarget | null): void {
  window.clearTimeout(resting);
  const url = inAppUrl(target);
  if (url) resting = window.setTimeout(() => warm(url), REST_MS);
}

/** For focus: warm the focused link now. */
export function warmNow(target: EventTarget | null): void {
  const url = inAppUrl(target);
  if (url) warm(url);
}
